import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UpdatePasswordPageProps,
  useLink,
  useRouterContext,
  useRouterType,
  useTranslate,
  useUpdatePassword,
} from '@refinedev/core';
import { ThemedTitle } from '../../../themedLayout/title';
import {
  layoutStyles,
  containerStyles,
  titleStyles,
  headStyles,
  bodyStyles,
  buttonStyle,
  cardHeaderStyles,
  PageTitleStyles,
} from './styles';
import {
  Row,
  Col,
  Layout,
  Card,
  Typography,
  Form,
  Button,
  LayoutProps,
  CardProps,
  FormProps,
  theme,
  Divider,
} from 'antd';
import { ConfirmPasswordForms } from './confirmPasswordForms';
import { ArrowLeftOutlined } from '@ant-design/icons';

const QUERY_PARAM_NAME = 'code';
type UpdatePasswordFormTypes = {
  password?: string;
  confirm?: string;
  code: string | null;
};
type UpdatePasswordProps = UpdatePasswordPageProps<LayoutProps, CardProps, FormProps>;

export const ResetPasswordPage: React.FC<UpdatePasswordProps> = ({
  wrapperProps,
  contentProps,
  renderContent,
  formProps,
  title,
}) => {
  const { Title } = Typography;
  const { useToken } = theme;
  const [form] = Form.useForm<UpdatePasswordFormTypes>();
  const translate = useTranslate();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();

  const { token } = useToken();
  const ActiveLink = routerType === 'legacy' ? LegacyLink : Link;

  const [searchParams] = useSearchParams();
  const { mutate: updatePassword, isLoading } = useUpdatePassword<UpdatePasswordFormTypes>();

  const onFinish = (values: UpdatePasswordFormTypes, code: string | null) => {
    updatePassword({ ...values, code });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const PageTitle =
    title === false ? null : (
      <div
        style={{
          ...PageTitleStyles,
        }}
      >
        {title ?? <ThemedTitle collapsed={false} />}
      </div>
    );

  const CardHeader = (
    <div
      style={{
        ...cardHeaderStyles,
        flexDirection: 'column',
      }}
    >
      <ActiveLink
        style={{
          color: token.colorPrimaryTextHover,
          fontSize: '14px',
          lineHeight: '22px',
          textAlign: 'left',
        }}
        to='/login'
      >
        <ArrowLeftOutlined className='site-form-item-icon' style={{ marginRight: '10px' }} />
        {translate('pages.login.buttons.Registration', 'Back')}
      </ActiveLink>
      <Divider style={{ marginTop: '15px' }} />
      <Title
        level={3}
        style={{
          color: token.colorPrimaryTextHover,
          ...titleStyles,
        }}
      >
        {translate('pages.enterNewPassword.title', 'Enter a new password')}
      </Title>
    </div>
  );

  const CardContent = (
    <Card
      title={CardHeader}
      headStyle={headStyles}
      bodyStyle={bodyStyles}
      style={{
        ...containerStyles,
      }}
      {...(contentProps ?? {})}
    >
      <Form<UpdatePasswordFormTypes>
        layout='vertical'
        form={form}
        requiredMark={false}
        {...formProps}
        onFinish={values => onFinish(values, searchParams.get(QUERY_PARAM_NAME))}
        onFinishFailed={onFinishFailed}
      >
        <ConfirmPasswordForms />
        <Row>
          <Col span={10} offset={7}>
            <Form.Item
              style={{
                marginBottom: 0,
              }}
            >
              <Button
                type='primary'
                size='large'
                htmlType='submit'
                loading={isLoading}
                block
                style={{ ...buttonStyle }}
              >
                {translate('pages.resetPassword.buttons.submit', 'Save')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  return (
    <Layout style={layoutStyles} {...(wrapperProps ?? {})}>
      <Row
        justify='center'
        align='middle'
        style={{
          height: '100vh',
        }}
      >
        <Col xs={22}>
          {renderContent ? (
            renderContent(CardContent, PageTitle)
          ) : (
            <>
              {PageTitle}
              {CardContent}
            </>
          )}
        </Col>
      </Row>
    </Layout>
  );
};
